import { useMemo } from 'react';
import dayjs from 'dayjs';
import { PhoneAnalyticsTypes } from '@frontend/api-analytics';
import { useAppScopeStore } from '@frontend/scope';
import { PhoneAnalyticsQueryKeys } from '../query-keys';

interface UseGetChartPayloadAndConfigProps {
  filters: PhoneAnalyticsTypes.Filters;
  isDemoAccount?: boolean;
  previousGlsLocationIds?: string;
  uniqueId: string;
}

export const useGetChartPayloadAndConfig = ({
  filters,
  isDemoAccount,
  previousGlsLocationIds,
  uniqueId,
}: UseGetChartPayloadAndConfigProps) => {
  const { selectedLocationIds } = useAppScopeStore();

  // Pick only required charts applicable filters
  const applicableFilters: Partial<PhoneAnalyticsTypes.Filters> = useMemo(
    () => ({
      end_date: filters?.end_date,
      location_id: filters?.location_id?.sort(), // Sort to avoid unnecessary API calls due to order change
      open_office: filters?.open_office,
      start_date: filters?.start_date,
      time_zone: filters?.time_zone,
    }),
    [filters]
  );

  // While generating query string, we need to make sure that we ignore the time string
  // It's required to avoid calling the API multiple times at the same time when the hook is invoked from multiple components at the same time
  // We have fixed stale time as 10 minutes so that we can avoid multiple API calls for the small timestamp changes
  const queryKey = useMemo(() => {
    if (
      !applicableFilters.location_id?.length ||
      !applicableFilters.start_date ||
      !applicableFilters.end_date ||
      (previousGlsLocationIds && [...selectedLocationIds].sort().join(',') !== previousGlsLocationIds) ||
      isDemoAccount
    ) {
      // We will not hit the api when query key is undefined
      return;
    }

    const startDate = dayjs(applicableFilters.start_date).format('YYYY-MM-DD');
    const endDate = dayjs(applicableFilters.end_date).format('YYYY-MM-DD');

    const filtersString = JSON.stringify({
      ...applicableFilters,
      start_date: startDate,
      end_date: endDate,
    });

    return PhoneAnalyticsQueryKeys.charts(`aggregated-${uniqueId}-${filtersString}`);
  }, [applicableFilters, isDemoAccount, uniqueId, selectedLocationIds, previousGlsLocationIds]);

  return {
    applicableFilters,
    config: {
      enabled: !!queryKey,
      queryKey,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 10 * 60 * 1000, // 10 minutes
    },
  };
};
